import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Badge } from '../ui/badge';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import {
  DollarSign, Users, Eye, TrendingUp, TrendingDown, Activity,
  Download, BarChart3, ArrowUpRight, ArrowDownRight, Send
} from 'lucide-react';
import { Button } from '../ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const COLORS = ['#D4AF37', '#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4'];

const AnalyticsDashboard = ({ revenueData, visitorData, trafficSources, dashboardData, hasPermission, exportData }) => {
  const [courseAnalytics, setCourseAnalytics] = useState(null);
  const [tgClicks, setTgClicks] = useState(null);

  useEffect(() => {
    fetchCourseAnalytics();
    fetchTelegramClicks();
  }, []);

  const fetchCourseAnalytics = async () => {
    try {
      const token = localStorage.getItem('admin_session_token');
      const res = await fetch(`${API_URL}/api/courses/admin/analytics?session_token=${token}`);
      if (res.ok) setCourseAnalytics(await res.json());
    } catch (e) { /* ignore */ }
  };

  const fetchTelegramClicks = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/telegram-click-stats`);
      if (res.ok) setTgClicks(await res.json());
    } catch (e) { /* ignore */ }
  };

  // Prepare chart data
  const monthlyData = (revenueData?.monthly_analytics || []).map(m => ({
    name: m.month,
    revenue: m.revenue || 0,
    subscribers: m.subscribers || 0,
    growth: m.growth_percentage || 0,
  }));

  const dailyData = (revenueData?.daily_analytics || []).slice(-14).map(d => ({
    date: d.date?.slice(5) || '',
    revenue: d.revenue || 0,
    subscribers: d.subscribers || 0,
  }));

  const visitorDailyData = (visitorData?.daily_stats || []).slice(-14).map(d => ({
    date: (d._id || d.date || '').slice(5),
    visits: d.total_visits || 0,
    unique: d.unique_visitors || 0,
  }));

  const trafficData = (trafficSources || []).map(s => ({
    name: (s._id || 'direct').charAt(0).toUpperCase() + (s._id || 'direct').slice(1),
    value: s.count || 0,
  }));

  const subBreakdown = dashboardData?.subscription_breakdown || {};
  const breakdownData = Object.entries(subBreakdown).filter(([_, v]) => v > 0).map(([k, v]) => ({
    name: k.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
    value: v,
  }));

  // KPI calculations
  const totalRevenue = revenueData?.total_revenue || 0;
  const totalSubs = revenueData?.total_subscribers || 0;
  const avgRevPerSub = totalSubs > 0 ? Math.round(totalRevenue / totalSubs) : 0;
  const currentMonth = revenueData?.current_month_revenue || 0;
  const prevMonth = monthlyData.length >= 2 ? monthlyData[monthlyData.length - 1]?.revenue : 0;
  const monthGrowth = monthlyData.length >= 2 ? monthlyData[monthlyData.length - 1]?.growth : 0;
  const totalVisitors = visitorData?.total_visitors || 0;
  const conversionRate = totalVisitors > 0 ? ((totalSubs / totalVisitors) * 100).toFixed(1) : 0;

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="bg-gray-900 border border-gray-700 rounded-lg p-3 shadow-xl">
        <p className="text-gray-400 text-xs mb-1">{label}</p>
        {payload.map((p, i) => (
          <p key={i} className="text-sm" style={{ color: p.color }}>
            {p.name}: {typeof p.value === 'number' && p.name.toLowerCase().includes('revenue')
              ? `₹${p.value.toLocaleString()}`
              : p.value}
          </p>
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="analytics-dashboard">
      {/* KPI Cards Row */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <KPICard icon={DollarSign} label="Total Revenue" value={`₹${totalRevenue.toLocaleString()}`} color="text-green-500" sub="All time" />
        <KPICard icon={Users} label="Total Subscribers" value={totalSubs} color="text-blue-500" sub="All packages" />
        <KPICard icon={Eye} label="Visitors (30d)" value={totalVisitors} color="text-purple-500" sub="Last 30 days" />
        <KPICard icon={TrendingUp} label="This Month" value={`₹${currentMonth.toLocaleString()}`} color="text-orange-500" sub={monthGrowth >= 0 ? `+${monthGrowth}%` : `${monthGrowth}%`} subColor={monthGrowth >= 0 ? 'text-green-500' : 'text-red-500'} />
        <KPICard icon={Activity} label="Avg Rev/Sub" value={`₹${avgRevPerSub.toLocaleString()}`} color="text-golden" sub="Per subscriber" />
        <KPICard icon={BarChart3} label="Conversion" value={`${conversionRate}%`} color="text-cyan-500" sub="Visitors → Subs" />
        <KPICard
          icon={Send}
          label="Telegram Joins"
          value={tgClicks?.total ?? 0}
          color="text-[#229ED9]"
          sub={`${tgClicks?.today ?? 0} today · ${tgClicks?.last_7_days ?? 0} in 7d`}
        />
      </div>

      {/* Charts Row 1: Revenue + Visitors */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Revenue Trend Chart */}
        <Card className="bg-white">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-gray-800">Revenue Trend</CardTitle>
            <CardDescription className="text-xs">Monthly revenue over time</CardDescription>
          </CardHeader>
          <CardContent>
            {monthlyData.length > 0 ? (
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={monthlyData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#6b7280' }} />
                  <YAxis tick={{ fontSize: 11, fill: '#6b7280' }} tickFormatter={v => `₹${(v/1000).toFixed(0)}k`} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="revenue" name="Revenue" fill="#D4AF37" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : <EmptyChart text="No revenue data yet" />}
          </CardContent>
        </Card>

        {/* Visitor Trend Chart */}
        <Card className="bg-white">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-gray-800">Visitor Trend (14 Days)</CardTitle>
            <CardDescription className="text-xs">Daily website visitors</CardDescription>
          </CardHeader>
          <CardContent>
            {visitorDailyData.length > 0 ? (
              <ResponsiveContainer width="100%" height={240}>
                <AreaChart data={visitorDailyData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#6b7280' }} />
                  <YAxis tick={{ fontSize: 11, fill: '#6b7280' }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Area type="monotone" dataKey="visits" name="Visits" stroke="#8B5CF6" fill="#8B5CF680" strokeWidth={2} />
                  <Area type="monotone" dataKey="unique" name="Unique" stroke="#3B82F6" fill="#3B82F640" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            ) : <EmptyChart text="No visitor data yet" />}
          </CardContent>
        </Card>
      </div>

      {/* Charts Row 2: Traffic Sources + Subscription Breakdown */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Traffic Sources Pie */}
        <Card className="bg-white">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-gray-800">Traffic Sources</CardTitle>
            <CardDescription className="text-xs">Where visitors come from (30 days)</CardDescription>
          </CardHeader>
          <CardContent>
            {trafficData.length > 0 ? (
              <div className="flex items-center gap-4">
                <ResponsiveContainer width="50%" height={200}>
                  <PieChart>
                    <Pie data={trafficData} cx="50%" cy="50%" innerRadius={45} outerRadius={80} dataKey="value" paddingAngle={2}>
                      {trafficData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex-1 space-y-1.5">
                  {trafficData.map((s, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm">
                      <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                      <span className="text-gray-700 flex-1">{s.name}</span>
                      <span className="text-gray-500 font-medium">{s.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : <EmptyChart text="No traffic data yet" />}
          </CardContent>
        </Card>

        {/* Subscription Breakdown Pie */}
        <Card className="bg-white">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-gray-800">Subscription Breakdown</CardTitle>
            <CardDescription className="text-xs">Distribution by package type</CardDescription>
          </CardHeader>
          <CardContent>
            {breakdownData.length > 0 ? (
              <div className="flex items-center gap-4">
                <ResponsiveContainer width="50%" height={200}>
                  <PieChart>
                    <Pie data={breakdownData} cx="50%" cy="50%" innerRadius={45} outerRadius={80} dataKey="value" paddingAngle={2}>
                      {breakdownData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex-1 space-y-1.5">
                  {breakdownData.map((s, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm">
                      <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                      <span className="text-gray-700 flex-1">{s.name}</span>
                      <span className="text-gray-500 font-medium">{s.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : <EmptyChart text="No subscription data yet" />}
          </CardContent>
        </Card>
      </div>

      {/* Monthly Subscribers Trend */}
      {monthlyData.length > 0 && (
        <Card className="bg-white">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-gray-800">Monthly Subscriber Growth</CardTitle>
            <CardDescription className="text-xs">New subscribers per month</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={monthlyData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#6b7280' }} />
                <YAxis tick={{ fontSize: 11, fill: '#6b7280' }} />
                <Tooltip content={<CustomTooltip />} />
                <Line type="monotone" dataKey="subscribers" name="Subscribers" stroke="#3B82F6" strokeWidth={2} dot={{ fill: '#3B82F6', r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Course Analytics Summary */}
      {courseAnalytics && courseAnalytics.overview.total_courses > 0 && (
        <Card className="bg-white">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-gray-800">Course Performance</CardTitle>
            <CardDescription className="text-xs">Enrollment and completion overview</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <MiniStat label="Total Courses" value={courseAnalytics.overview.total_courses} />
              <MiniStat label="Enrollments" value={courseAnalytics.overview.total_enrollments} />
              <MiniStat label="Completions" value={courseAnalytics.overview.total_completed} />
              <MiniStat label="Completion Rate" value={`${courseAnalytics.overview.overall_completion_rate}%`} />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Export Actions */}
      <Card className="bg-white">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold text-gray-800">Export Data</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3">
            <Button onClick={() => exportData('payments')} variant="outline" size="sm" className="border-gray-300">
              <Download className="w-3.5 h-3.5 mr-1.5" /> Payments
            </Button>
            <Button onClick={() => exportData('visitors')} variant="outline" size="sm" className="border-gray-300">
              <Download className="w-3.5 h-3.5 mr-1.5" /> Visitors
            </Button>
            <Button onClick={() => exportData('coupons')} variant="outline" size="sm" className="border-gray-300">
              <Download className="w-3.5 h-3.5 mr-1.5" /> Coupons
            </Button>
            <Button onClick={() => window.open(`${API_URL}/api/admin/payments/export-csv`, '_blank')} variant="outline" size="sm" className="border-green-300 text-green-700">
              <Download className="w-3.5 h-3.5 mr-1.5" /> Full CSV Export
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};


const KPICard = ({ icon: Icon, label, value, color, sub, subColor }) => (
  <Card className="bg-white" data-testid={`kpi-${label.toLowerCase().replace(/\s/g, '-')}`}>
    <CardContent className="p-4">
      <div className="flex items-center gap-2 mb-1">
        <Icon className={`w-4 h-4 ${color}`} />
        <span className="text-xs text-gray-500">{label}</span>
      </div>
      <p className="text-xl font-bold text-gray-900">{value}</p>
      {sub && <p className={`text-[11px] mt-0.5 ${subColor || 'text-gray-400'}`}>{sub}</p>}
    </CardContent>
  </Card>
);

const MiniStat = ({ label, value }) => (
  <div className="bg-gray-50 rounded-lg p-3 text-center">
    <p className="text-lg font-bold text-gray-900">{value}</p>
    <p className="text-xs text-gray-500">{label}</p>
  </div>
);

const EmptyChart = ({ text }) => (
  <div className="h-[200px] flex items-center justify-center text-gray-400 text-sm">{text}</div>
);

export default AnalyticsDashboard;
